import {
  ArrowDown,
  ArrowUp,
  Building2,
  CalendarClock,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Newspaper,
  RefreshCw,
} from "lucide-react";
import { formatDate, formatMoney, quoteIsStale } from "./market";
import type {
  EarningsResponse,
  NewsResponse,
  StockQuote,
  WatchlistResponse,
} from "./types";

export function WatchlistStatus({
  data,
  refreshing,
  refresh,
}: {
  data: WatchlistResponse;
  refreshing: boolean;
  refresh: () => void;
}) {
  const stale = data.stale || quoteIsStale(data.timestamp);
  const age = Math.max(0, Math.floor((Date.now() - data.timestamp * 1000) / 60000));
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-white/40">
      <span
        className={`flex items-center gap-1.5 ${stale ? "text-[#E53935]" : "text-[#1FBF75]"}`}
      >
        {stale ? <Clock3 className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        {stale
          ? "Daten möglicherweise veraltet"
          : age < 1
            ? "Gerade aktualisiert"
            : `Aktualisiert vor ${age} Min.`}
      </span>
      <button
        onClick={refresh}
        className="flex h-9 items-center gap-2 border border-white/10 bg-[#07080d] px-3 font-medium text-white"
      >
        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        Aktualisieren
      </button>
    </div>
  );
}

export function StockCard({
  quote,
  selected,
  onSelect,
}: {
  quote: StockQuote;
  selected: boolean;
  onSelect: () => void;
}) {
  const positive = quote.changePercent >= 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`border p-5 text-left transition-colors ${selected ? "border-[#e01e37] bg-[#07080d]" : "border-white/10 bg-[#07080d] hover:border-white/20"}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {quote.logo && (
            <img
              src={quote.logo}
              alt=""
              className="h-8 w-8 rounded-full bg-white/5 object-contain"
            />
          )}
          <div>
            <p className="text-sm font-semibold text-white">{quote.name}</p>
            <p className="mt-0.5 font-mono text-xs text-white/40">{quote.symbol}</p>
          </div>
        </div>
        <span
          className={`flex items-center gap-1 text-xs font-semibold ${positive ? "text-[#1FBF75]" : "text-[#E53935]"}`}
        >
          {positive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          {quote.changePercent.toLocaleString("de-DE", { maximumFractionDigits: 2 })}%
        </span>
      </div>
      <p className="mt-6 font-display text-2xl font-bold tabular-nums text-white">
        {formatMoney(quote.price)}
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-white/40">
        <span>Hoch: {formatMoney(quote.high)}</span>
        <span>Tief: {formatMoney(quote.low)}</span>
        <span>Eröffnung: {formatMoney(quote.open)}</span>
        <span>Vortag: {formatMoney(quote.previousClose)}</span>
      </div>
      <p className="mt-3 text-[11px] text-white/30">{formatDate(quote.timestamp)}</p>
    </button>
  );
}

export function CompanyPanel({ quote }: { quote: StockQuote }) {
  return (
    <section className="border border-white/10 bg-[#07080d] p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center bg-[#e01e37]/10 text-[#e01e37]">
          <Building2 className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-xl font-bold text-white">{quote.name}</h2>
          <p className="text-sm text-white/40">{quote.symbol}</p>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
        <Field label="Branche" value={quote.industry ?? "Nicht verfügbar"} />
        <Field label="Börse" value={quote.exchange ?? "Nicht verfügbar"} />
        <Field label="Land" value={quote.country ?? "Nicht verfügbar"} />
        <Field
          label="Marktkapitalisierung"
          value={
            quote.marketCapitalization
              ? `${quote.marketCapitalization.toLocaleString("de-DE", { maximumFractionDigits: 0 })} Mio. USD`
              : "Nicht verfügbar"
          }
        />
      </div>
      {quote.website && (
        <a
          href={quote.website}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#e01e37]"
        >
          Website besuchen <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </section>
  );
}

export function EarningsPanel({
  earnings,
  loading,
}: {
  earnings: EarningsResponse | null;
  loading: boolean;
}) {
  return (
    <section className="border border-white/10 bg-[#07080d] p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center bg-[#e01e37]/10 text-[#e01e37]">
          <CalendarClock className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-xl font-bold text-white">Earnings</h2>
          <p className="text-sm text-white/40">Historie und nächster Termin</p>
        </div>
      </div>
      {loading ? (
        <div className="mt-6 h-24 animate-pulse bg-white/[.03]" />
      ) : !earnings || (!earnings.history.length && !earnings.upcoming) ? (
        <p className="mt-6 text-sm text-white/40">Keine Earnings-Daten verfügbar.</p>
      ) : (
        <>
          <p className="mt-6 text-sm text-white/40">
            Nächster Termin:{" "}
            <span className="text-white">{earnings.upcoming?.date ?? "Nicht bekannt"}</span>
          </p>
          {earnings.history.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[480px] text-left text-sm">
                <thead className="text-xs uppercase tracking-wider text-white/40">
                  <tr>
                    <th className="py-2 pr-4">Periode</th>
                    <th className="py-2 pr-4">Erwartet</th>
                    <th className="py-2 pr-4">Ist</th>
                    <th className="py-2 pr-4">Überraschung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {earnings.history.map((entry) => (
                    <tr key={entry.period}>
                      <td className="py-2 pr-4">{entry.period}</td>
                      <td className="py-2 pr-4 tabular-nums">
                        {entry.estimate ?? "–"}
                      </td>
                      <td className="py-2 pr-4 tabular-nums">{entry.actual ?? "–"}</td>
                      <td
                        className={`py-2 pr-4 tabular-nums ${entry.surprisePercent !== null && entry.surprisePercent >= 0 ? "text-[#1FBF75]" : "text-[#E53935]"}`}
                      >
                        {entry.surprisePercent !== null
                          ? `${entry.surprisePercent.toLocaleString("de-DE", { maximumFractionDigits: 2 })}%`
                          : "–"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function NewsPanel({ news, loading }: { news: NewsResponse | null; loading: boolean }) {
  return (
    <section className="border border-white/10 bg-[#07080d] p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center bg-[#e01e37]/10 text-[#e01e37]">
          <Newspaper className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-xl font-bold text-white">Unternehmensnachrichten</h2>
          <p className="text-sm text-white/40">Letzte 7 Tage</p>
        </div>
      </div>
      {loading ? (
        <div className="mt-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse bg-white/[.03]" />
          ))}
        </div>
      ) : !news || !news.items.length ? (
        <p className="mt-6 text-sm text-white/40">Keine aktuellen Nachrichten verfügbar.</p>
      ) : (
        <ul className="mt-6 space-y-4">
          {news.items.map((item) => (
            <li key={item.url} className="flex gap-4 border-b border-white/10 pb-4 last:border-0">
              {item.image && (
                <img
                  src={item.image}
                  alt=""
                  className="h-16 w-24 flex-none bg-white/5 object-cover"
                />
              )}
              <div className="min-w-0">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="line-clamp-2 text-sm font-semibold text-white hover:text-[#e01e37]"
                >
                  {item.headline}
                </a>
                <p className="mt-1 text-xs text-white/40">
                  {item.source} · {formatDate(item.datetime)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-white/40">{label}</p>
      <p className="mt-1 font-semibold text-white">{value}</p>
    </div>
  );
}
